// main.js — Shared utilities loaded on every page.
// Handles: localStorage persistence, web component coordination, bfcache restore.
// Page-specific code lives in property.js, search.js, and components/*.js

const STORAGE_KEY = 'bookingWorldOne';
const SAVED_PROPERTIES_KEY = 'bookingWorldOne_savedProperties';

// ============================================
// AUTH & FAVORITES HELPERS
// ============================================

function getSavedProperties() {
    try {
        var data = localStorage.getItem(SAVED_PROPERTIES_KEY);
        return data ? JSON.parse(data) : {};
    } catch (e) { return {}; }
}

function saveSavedProperties(props) {
    try {
        localStorage.setItem(SAVED_PROPERTIES_KEY, JSON.stringify(props));
    } catch (e) { console.error('Error saving properties:', e); }
}

function logout() {
    fetch('/api/auth/logout', { method: 'POST' })
    .finally(function() {
        // Same-tab: clear the <user-menu> sessionStorage cache so the
        // post-reload page re-fetches /api/auth/me and renders anonymous.
        try { sessionStorage.removeItem('user_me_v1'); } catch (e) {}
        // Cross-tab: storage event tells other tabs' <user-menu> to invalidate.
        try { localStorage.setItem('auth_change', String(Date.now())); } catch (e) {}
        window.location.reload();
    });
}

// Sync localStorage favorites to server after login/register.
// Posts local items to /api/favorites/sync, then updates localStorage from server response.
function syncFavoritesAfterAuth() {
    var local = getSavedProperties();
    var items = [];
    for (var id in local) {
        if (local.hasOwnProperty(id)) {
            items.push({
                property_id: id,
                name: local[id].name || '',
                image: local[id].image || '',
                address: local[id].address || '',
                url: local[id].url || '',
                saved_at: local[id].savedAt || new Date().toISOString()
            });
        }
    }
    return fetch('/api/favorites/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(items)
    })
    .then(function(resp) {
        if (!resp.ok) return;
        return resp.json();
    })
    .then(function(serverItems) {
        if (!serverItems) return;
        // Rebuild localStorage from server (authoritative)
        var merged = {};
        for (var i = 0; i < serverItems.length; i++) {
            var item = serverItems[i];
            merged[item.property_id] = {
                name: item.name || '',
                image: item.image || '',
                address: item.address || '',
                url: item.url || '',
                savedAt: item.saved_at || new Date().toISOString()
            };
        }
        saveSavedProperties(merged);
    })
    .catch(function(e) { console.error('Favorites sync error:', e); });
}

// Save or unsave a favorite, dual-writing to localStorage + server API.
function saveFavoriteToServer(propertyId, data) {
    fetch('/api/favorites/' + propertyId, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data || {})
    }).then(function(resp) {
        if (!resp.ok && resp.status === 401) {
            var saved = getSavedProperties();
            delete saved[propertyId];
            saveSavedProperties(saved);
        }
    }).catch(function(err) {
        console.error('Save favorite error:', err);
    });
}

function unsaveFavoriteFromServer(propertyId) {
    fetch('/api/favorites/' + propertyId, { method: 'DELETE' })
    .catch(function(err) { console.error('Unsave error:', err); });
}

// ============================================
// LOCAL STORAGE HELPERS
// ============================================

function getStoredData() {
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        return data ? JSON.parse(data) : null;
    } catch (e) { return null; }
}

function saveSearchParams(params) {
    try {
        const data = getStoredData() || {};
        data.searchParams = { ...data.searchParams, ...params };
        data.updatedAt = new Date().toISOString();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        console.error('Error saving to localStorage:', e);
    }
}

// ============================================
// RESTORE STATE FROM URL / LOCALSTORAGE
// ============================================

// Check if localStorage has newer search params than the current URL.
// If so, redirect with updated params so the server returns fresh results.
// Returns true if a redirect was triggered.
function redirectIfStaleParams(saved) {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has('checkin') || urlParams.get('checkin') === '') return false;
    if (!saved) return false;

    const paramsChanged =
        (saved.checkIn && saved.checkIn !== urlParams.get('checkin')) ||
        (saved.checkOut && saved.checkOut !== urlParams.get('checkout')) ||
        (urlParams.has('adults') && saved.adults && String(saved.adults) !== urlParams.get('adults')) ||
        (urlParams.has('children') && saved.children !== undefined && String(saved.children) !== urlParams.get('children')) ||
        (urlParams.has('rooms') && saved.rooms && String(saved.rooms) !== urlParams.get('rooms'));

    if (!paramsChanged) return false;

    const newParams = new URLSearchParams(urlParams);
    if (saved.checkIn) newParams.set('checkin', saved.checkIn);
    if (saved.checkOut) newParams.set('checkout', saved.checkOut);
    if (saved.adults) newParams.set('adults', saved.adults);
    if (saved.children !== undefined) newParams.set('children', saved.children);
    if (saved.rooms) newParams.set('rooms', saved.rooms);
    // Also carry destination from localStorage if URL doesn't have it
    if (saved.destination_id && !urlParams.get('destination_id')) {
        newParams.set('destination_id', saved.destination_id);
        newParams.set('destination_type', saved.destination_type || 'city');
        newParams.set('destination', saved.destination || '');
    }
    window.location.replace(window.location.pathname + '?' + newParams.toString());
    return true;
}

function loadSavedSearchParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const hasURLDates = urlParams.has('checkin') && urlParams.get('checkin') !== '';
    const hasURLGuests = urlParams.has('adults');

    const stored = getStoredData();
    const saved = (stored && stored.searchParams) ? stored.searchParams : null;

    // Clear past dates from localStorage
    if (saved) {
        const today = new Date().toISOString().split('T')[0];
        if (saved.checkIn && saved.checkIn < today) {
            saved.checkIn = null;
            saved.checkOut = null;
        }
    }

    // On back/forward navigation, localStorage holds the most recent user-set values.
    // If they differ from the stale URL, redirect with updated params.
    const navEntry = performance.getEntriesByType('navigation')[0];
    const isBackForward = navEntry && navEntry.type === 'back_forward';
    if (isBackForward && saved && redirectIfStaleParams(saved)) return;

    // Save URL params to localStorage if present
    if (hasURLDates) {
        var params = {
            checkIn: urlParams.get('checkin'),
            checkOut: urlParams.get('checkout'),
            adults: parseInt(urlParams.get('adults')) || 2,
            children: parseInt(urlParams.get('children')) || 0,
            rooms: parseInt(urlParams.get('rooms')) || 1,
        };
        // Only save destination fields if they're actually in the URL
        // (property pages don't have destination params — don't overwrite with empty)
        if (urlParams.get('destination_id')) {
            const destEl = document.querySelector('destination-autocomplete');
            params.destination = destEl ? (destEl.querySelector('.da-input')?.value || '') : (urlParams.get('destination') || '');
            params.destination_id = urlParams.get('destination_id');
            params.destination_type = urlParams.get('destination_type') || '';
            params.latitude = urlParams.get('latitude') || '';
            params.longitude = urlParams.get('longitude') || '';
        }
        saveSearchParams(params);
    }

    if (hasURLGuests && saved) {
        saveSearchParams({
            adults: parseInt(urlParams.get('adults')) || 2,
            children: parseInt(urlParams.get('children')) || 0,
            rooms: parseInt(urlParams.get('rooms')) || 1
        });
    }

    // Restore dates from localStorage if URL doesn't have them
    if (saved && !hasURLDates) {
        document.querySelectorAll('date-range-picker').forEach(picker => {
            const ci = picker.querySelector('.drp-checkin');
            const co = picker.querySelector('.drp-checkout');
            if (ci && !ci.value && saved.checkIn) {
                ci.value = saved.checkIn;
                co.value = saved.checkOut || '';
                picker._updateDisplay();
            }
        });
    }

    // Restore destination from localStorage if component doesn't have a destination_id.
    // This is separate from dates because property pages have URL dates but no destination.
    const hasURLDest = urlParams.has('destination_id') && urlParams.get('destination_id') !== '';
    if (saved && saved.destination_id && !hasURLDest) {
        document.querySelectorAll('destination-autocomplete').forEach(da => {
            const idField = da.querySelector('.da-id');
            if (idField && !idField.value) {
                idField.value = saved.destination_id;
                da.querySelector('.da-type').value = saved.destination_type || '';
                da.querySelector('.da-lat').value = saved.latitude || '';
                da.querySelector('.da-lng').value = saved.longitude || '';
                // Also restore destination text if the input is empty
                const input = da.querySelector('.da-input');
                if (input && !input.value && saved.destination) {
                    input.value = saved.destination;
                }
                da._toggleClear();
            }
        });
    }

    if (saved && !hasURLGuests) {
        // Restore guests into <guest-selector> components
        document.querySelectorAll('guest-selector').forEach(gs => {
            const adultsInput = gs.querySelector('.gs-adults');
            const childrenInput = gs.querySelector('.gs-children');
            const roomsInput = gs.querySelector('.gs-rooms');

            if (adultsInput && saved.adults && parseInt(adultsInput.value) === 2) {
                adultsInput.value = saved.adults;
                const d = gs.querySelector('.gs-val[data-type="adults"]');
                if (d) d.textContent = saved.adults;
            }
            if (childrenInput && saved.children !== undefined && parseInt(childrenInput.value) === 0) {
                childrenInput.value = saved.children;
                const d = gs.querySelector('.gs-val[data-type="children"]');
                if (d) d.textContent = saved.children;
            }
            if (roomsInput && saved.rooms && parseInt(roomsInput.value) === 1) {
                roomsInput.value = saved.rooms;
                const d = gs.querySelector('.gs-val[data-type="rooms"]');
                if (d) d.textContent = saved.rooms;
            }
            gs._refreshDisplay();
        });
    }
}

// ============================================
// EVENT LISTENERS: Persist changes from web components
// ============================================

document.addEventListener('destination-change', function(e) {
    // Don't persist unresolved state (has text but no ID) — wait for selection or auto-resolve.
    // Allow clearing (both empty) and resolved selections (both present).
    if (e.detail.destination && !e.detail.destination_id) return;
    saveSearchParams({
        destination: e.detail.destination,
        destination_id: e.detail.destination_id,
        destination_type: e.detail.destination_type,
        latitude: e.detail.latitude,
        longitude: e.detail.longitude,
    });
});

document.addEventListener('dates-change', function(e) {
    saveSearchParams({
        checkIn: e.detail.checkin,
        checkOut: e.detail.checkout,
    });

    // Sync all date-range-picker instances on the page
    document.querySelectorAll('date-range-picker').forEach(picker => {
        const ci = picker.querySelector('.drp-checkin');
        const co = picker.querySelector('.drp-checkout');
        if (ci && ci.value !== e.detail.checkin) {
            ci.value = e.detail.checkin;
            co.value = e.detail.checkout;
            picker._updateDisplay();
        }
    });
});

document.addEventListener('guests-change', function(e) {
    saveSearchParams({
        adults: e.detail.adults,
        children: e.detail.children,
        rooms: e.detail.rooms,
    });

    // Sync all guest-selector instances on the page
    document.querySelectorAll('guest-selector').forEach(gs => {
        const a = gs.querySelector('.gs-adults');
        if (a && parseInt(a.value) !== e.detail.adults) {
            a.value = e.detail.adults;
            gs.querySelector('.gs-children').value = e.detail.children;
            gs.querySelector('.gs-rooms').value = e.detail.rooms;
            // Update counter displays
            gs.querySelector('.gs-val[data-type="adults"]').textContent = e.detail.adults;
            gs.querySelector('.gs-val[data-type="children"]').textContent = e.detail.children;
            gs.querySelector('.gs-val[data-type="rooms"]').textContent = e.detail.rooms;
            gs._refreshDisplay();
        }
    });
});

// Save on search form submission
document.addEventListener('submit', function(e) {
    if (e.target.matches('form[action="/search"]') || e.target.matches('form[action^="/property/"]')) {
        const formData = new FormData(e.target);
        var params = {};
        // Only save destination fields if the form actually has them
        // (property page form only has dates + guests, not destination)
        if (formData.has('destination')) {
            params.destination = formData.get('destination') || '';
            params.destination_id = formData.get('destination_id') || '';
            params.destination_type = formData.get('destination_type') || '';
            params.latitude = formData.get('latitude') || '';
            params.longitude = formData.get('longitude') || '';
        }
        if (formData.has('checkin')) {
            params.checkIn = formData.get('checkin') || '';
            params.checkOut = formData.get('checkout') || '';
        }
        if (formData.has('adults')) {
            params.adults = parseInt(formData.get('adults')) || 2;
            params.children = parseInt(formData.get('children')) || 0;
            params.rooms = parseInt(formData.get('rooms')) || 1;
        }
        saveSearchParams(params);
    }
});

// Handle bfcache restore (browser back/forward)
window.addEventListener('pageshow', function(e) {
    if (e.persisted) {
        const stored = getStoredData();
        if (!stored || !stored.searchParams) return;
        const saved = stored.searchParams;

        const today = new Date().toISOString().split('T')[0];
        if (saved.checkIn && saved.checkIn < today) return;

        // If localStorage has newer params, redirect instead of just updating components
        if (redirectIfStaleParams(saved)) return;

        // Restore into web components
        document.querySelectorAll('date-range-picker').forEach(picker => {
            const ci = picker.querySelector('.drp-checkin');
            const co = picker.querySelector('.drp-checkout');
            if (ci && saved.checkIn) { ci.value = saved.checkIn; co.value = saved.checkOut; picker._updateDisplay(); }
        });

        document.querySelectorAll('guest-selector').forEach(gs => {
            if (saved.adults) { gs.querySelector('.gs-adults').value = saved.adults; }
            if (saved.children !== undefined) { gs.querySelector('.gs-children').value = saved.children; }
            if (saved.rooms) { gs.querySelector('.gs-rooms').value = saved.rooms; }
            gs._refreshDisplay();
        });
    }
});

// ============================================
// HOMEPAGE LINK HELPERS
// ============================================

// Enhance property type / deals / destination links with stored search params
function enhanceSearchLink(event, extraParams) {
    event.preventDefault();
    const stored = getStoredData();
    const saved = (stored && stored.searchParams) ? stored.searchParams : null;
    const url = new URL('/search', location.origin);

    // Add extra params (e.g. property_type, destination_id)
    for (const [k, v] of Object.entries(extraParams || {})) {
        if (v) url.searchParams.set(k, v);
    }

    // Use stored destination if not already set by extraParams
    if (!url.searchParams.has('destination_id') && saved && saved.destination_id) {
        url.searchParams.set('destination_id', saved.destination_id);
        url.searchParams.set('destination_type', saved.destination_type || 'city');
        url.searchParams.set('destination', saved.destination || '');
    }

    // Ensure dates exist — default to 3 days out, 1 night (like Booking.com)
    var today = new Date();
    var ci = (saved && saved.checkIn) ? saved.checkIn : new Date(today.getTime() + 3*86400000).toISOString().split('T')[0];
    var co = (saved && saved.checkOut) ? saved.checkOut : new Date(today.getTime() + 4*86400000).toISOString().split('T')[0];
    // Skip past dates
    var todayStr = today.toISOString().split('T')[0];
    if (ci < todayStr) {
        ci = new Date(today.getTime() + 3*86400000).toISOString().split('T')[0];
        co = new Date(today.getTime() + 4*86400000).toISOString().split('T')[0];
    }
    url.searchParams.set('checkin', ci);
    url.searchParams.set('checkout', co);
    url.searchParams.set('adults', (saved && saved.adults) ? saved.adults : 2);
    url.searchParams.set('rooms', (saved && saved.rooms) ? saved.rooms : 1);

    location.href = url.toString();
    return false;
}

// ============================================
// INIT
// ============================================

document.addEventListener('DOMContentLoaded', function() {
    loadSavedSearchParams();
});
